import { useCallback, useEffect, useRef, useState } from 'react'
import type { WidgetDisplay } from '../lib/types'
import { plural } from '../lib/plural'
import {
  fetchSeparationBatch,
  fetchSeparationGroup,
  finishSeparationGroups,
  reportGoneGroups,
  saveSeparationAnswer,
  UNSURE_VALUE,
  type SeparationBatch,
  type SeparationGroup,
  type SeparationSubmission,
} from '../components/widgets/separation'

interface SortTally {
  business: number
  personal: number
  transfers: number
  split: number
  handedOff: number
  alreadySorted: number
}

const EMPTY_TALLY: SortTally = {
  business: 0,
  personal: 0,
  transfers: 0,
  split: 0,
  handedOff: 0,
  alreadySorted: 0,
}

const answeredOf = (tally: SortTally): number =>
  tally.business + tally.personal + tally.transfers + tally.split

const tallyAnswer = (
  tally: SortTally,
  group: SeparationGroup,
  submission: SeparationSubmission
): SortTally => {
  if ('rows' in submission) return { ...tally, split: tally.split + 1 }
  if (submission.answer === UNSURE_VALUE)
    return { ...tally, handedOff: tally.handedOff + 1 }
  if (group.kind === 'money_movement' && submission.answer !== 'business')
    return { ...tally, transfers: tally.transfers + 1 }
  if (submission.answer === 'personal')
    return { ...tally, personal: tally.personal + 1 }
  return { ...tally, business: tally.business + 1 }
}

type SortingEnd = 'last' | 'capped' | 'open'

const ENDING_SENTENCE: Record<SortingEnd, string> = {
  last: 'That was the last of them.',
  capped: 'Heard will sort the rest automatically.',
  open: '',
}

const toClosingSentence = (tally: SortTally, end: SortingEnd): string => {
  const answered = answeredOf(tally)
  const parts = [
    tally.business && `${tally.business} business`,
    tally.personal && `${tally.personal} personal`,
    tally.transfers && plural(tally.transfers, 'transfer'),
    tally.split && `${tally.split} split row by row`,
  ].filter(Boolean)

  const clauses = [
    answered
      ? `I sorted ${plural(answered, 'group')}: ${parts.join(', ')}.`
      : '',
    tally.handedOff
      ? `I sent ${plural(tally.handedOff, 'group')} to a bookkeeper with a note.`
      : '',
    tally.alreadySorted
      ? `${plural(tally.alreadySorted, 'group')} had already been sorted.`
      : '',
  ].filter(Boolean)
  if (!clauses.length) return 'Nothing was left to sort.'
  return [...clauses, ENDING_SENTENCE[end]].filter(Boolean).join(' ')
}

const toClosingDisplay = (tally: SortTally): WidgetDisplay => {
  const sorted = answeredOf(tally) + tally.handedOff
  if (!sorted) return { label: 'Nothing left to sort.' }
  const breakdown = [
    tally.business && `${tally.business} business`,
    tally.personal && `${tally.personal} personal`,
    tally.transfers && plural(tally.transfers, 'transfer'),
    tally.split && `${tally.split} mixed`,
    tally.handedOff && `${tally.handedOff} to a bookkeeper`,
  ]
    .filter(Boolean)
    .join(', ')
  return { label: `Sorted ${plural(sorted, 'merchant')}: ${breakdown}` }
}

export interface MerchantSorting {
  group: SeparationGroup | null
  progress: { position: number; total: number } | null
  loadFailed: boolean
  retry: () => void
  answer: (submission: SeparationSubmission) => Promise<void>
  saving: boolean
  saveFailed: boolean
  reportRefused: boolean
  resendReport: () => void
}

interface ClosingReport {
  text: string
  display: WidgetDisplay
}

/**
 * Walks the groups to sort, one at a time, and reports to Haven once at the end. Keys that
 * join the batch are appended up to the server's cap; past it the rest go to autocat.
 */
export const useMerchantSorting = (
  initialKeys: string[],
  onOutcome: (text: string, display?: WidgetDisplay) => void,
  resolved: boolean
): MerchantSorting => {
  const [keys, setKeys] = useState<string[]>(initialKeys)
  const keysRef = useRef(initialKeys)
  const [index, setIndex] = useState<number | null>(null)
  // Keys whose group was already answered, e.g. in another chat: the counter leaves them out.
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set())
  const checked = useRef<Set<string>>(new Set())
  const markSkipped = useCallback(
    (key: string) => setSkipped((current) => new Set(current).add(key)),
    []
  )
  const [group, setGroup] = useState<SeparationGroup | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  const tally = useRef<SortTally>(EMPTY_TALLY)
  const failedAt = useRef<number | null>(null)
  const closed = useRef(false)
  const [report, setReport] = useState<ClosingReport | null>(null)

  const savingRef = useRef(false)

  const learnBatch = useCallback(
    (batch: Pick<SeparationBatch, 'groupKeys' | 'maxQuestions'>) => {
      const room = Math.max(batch.maxQuestions - keysRef.current.length, 0)
      const admitted = batch.groupKeys
        .filter((key) => !keysRef.current.includes(key))
        .slice(0, room)
      if (admitted.length) {
        keysRef.current = [...keysRef.current, ...admitted]
        setKeys(keysRef.current)
      }
    },
    []
  )

  const close = useCallback(
    (end: SortingEnd) => {
      if (closed.current) return
      closed.current = true
      const closing = {
        text: toClosingSentence(tally.current, end),
        display: toClosingDisplay(tally.current),
      }
      setReport(closing)
      setIndex(null)
      setGroup(null)
      onOutcome(closing.text, closing.display)
    },
    [onOutcome]
  )

  const openFrom = useCallback(
    async (from: number): Promise<void> => {
      let next = from
      for (;;) {
        for (; next < keysRef.current.length; next += 1) {
          let loaded: SeparationGroup | null
          try {
            loaded = await fetchSeparationGroup(keysRef.current[next] ?? '')
          } catch {
            failedAt.current = next
            setLoadFailed(true)
            return
          }
          if (!loaded) {
            markSkipped(keysRef.current[next] ?? '')
            continue
          }
          failedAt.current = null
          setLoadFailed(false)
          learnBatch(loaded)
          setGroup(loaded)
          setIndex(next)
          return
        }
        // Haven's keys can all be answered while later groups still wait, so ask before closing.
        const known = keysRef.current.length
        let batch: SeparationBatch
        try {
          batch = await fetchSeparationBatch()
        } catch {
          failedAt.current = next
          setLoadFailed(true)
          return
        }
        learnBatch(batch)
        if (keysRef.current.length !== known) continue
        if (batch.groupKeys.length === 0 && batch.groupsNotShown === 0) {
          close('last')
          return
        }
        if (known < batch.maxQuestions) {
          close('open')
          return
        }
        try {
          await finishSeparationGroups()
        } catch {
          failedAt.current = next
          setLoadFailed(true)
          return
        }
        close('capped')
        return
      }
    },
    [close, learnBatch, markSkipped]
  )

  const opened = useRef(false)
  useEffect(() => {
    if (opened.current || resolved) return
    opened.current = true
    void openFrom(0)
  }, [openFrom, resolved])

  // A key answered elsewhere must leave the total before the member reaches it.
  useEffect(() => {
    if (resolved) return
    const unchecked = keys.filter((key) => !checked.current.has(key))
    unchecked.forEach((key) => checked.current.add(key))
    reportGoneGroups(unchecked, markSkipped)
  }, [keys, resolved, markSkipped])

  const answer = async (submission: SeparationSubmission): Promise<void> => {
    if (!group || index === null || savingRef.current) return
    savingRef.current = true
    setSaving(true)
    setSaveFailed(false)
    try {
      const saved = await saveSeparationAnswer(group.groupKey, submission)
      if (!saved) {
        tally.current = {
          ...tally.current,
          alreadySorted: tally.current.alreadySorted + 1,
        }
      } else {
        tally.current = saved.alreadySettled
          ? { ...tally.current, alreadySorted: tally.current.alreadySorted + 1 }
          : tallyAnswer(tally.current, group, submission)
        learnBatch(saved)
        if (saved.finished) {
          close('last')
          return
        }
      }
      await openFrom(index + 1)
    } catch {
      setSaveFailed(true)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return {
    group,
    progress:
      index === null
        ? null
        : {
            position:
              keys.slice(0, index).filter((key) => !skipped.has(key)).length +
              1,
            total: keys.filter((key) => !skipped.has(key)).length,
          },
    loadFailed,
    retry: () => {
      setLoadFailed(false)
      void openFrom(failedAt.current ?? 0)
    },
    answer,
    saving,
    saveFailed,
    reportRefused: report !== null && !resolved,
    resendReport: () => {
      if (report) onOutcome(report.text, report.display)
    },
  }
}
